import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { Circle, Ellipse, Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from 'react-konva';
import { boundsOf, estimateSeatRadius, labelAnchor, labelFrame, pointInBounds, pointInPolygon, round2, rowAxis, shapesOf } from './geometry';
import { autoPlaceGaBlocks } from './ga-layout';
import type { Point } from './geometry';
import type { AvailabilityMap, GaAreaGeometry, ManifestGeometry, PolygonGeometry, SeatGeometry, SectionGeometry, TableGeometry } from './types';

/**
 * The seat map, drawn from manifest geometry (spec §6): the same component
 * serves the customer storefront, the box office and the manifest editor,
 * differing only in what it is told and what it reports back.
 *
 * It never decides availability. In `view` mode it colors seats from the
 * availability map it is handed; in `edit` mode it colors them by attribute
 * and reports clicks, marquee selections, drags, row handles, canvas clicks
 * and drawn polygons so the editor around it can act. Nothing is written from
 * here: every gesture ends in a callback, and the map re-renders from whatever
 * the caller stores. Konva is imported here and nowhere else in BRE code.
 */

export type SeatMapMode = 'view' | 'edit';

/** Drawing a new boundary, or dragging the vertices of an existing one. */
export type PolygonTool = { mode: 'draw' } | { mode: 'edit'; points: Point[] };

export interface SeatMapViewProps {
  layout: ManifestGeometry;
  mode?: SeatMapMode;
  /** Absolute or same-origin URL of the background drawing; fetched with credentials. */
  backgroundUrl?: string | null;
  availability?: AvailabilityMap;
  selectedSeatIds?: ReadonlySet<string>;
  /** Seats this customer already holds (an exchange): drawn as theirs, and not selectable. */
  ownedSeatIds?: ReadonlySet<string> | undefined;
  onSeatClick?: ((seat: SeatGeometry, modifiers: { shiftKey: boolean; metaKey: boolean }) => void) | undefined;
  /** Edit mode: shift-drag on the canvas. */
  onMarqueeSelect?: ((seatIds: string[], additive: boolean) => void) | undefined;
  /** A click on empty canvas, in canvas units — used to place rows, tables and single seats. */
  onCanvasClick?: ((point: Point) => void) | undefined;
  onSectionClick?: ((section: SectionGeometry) => void) | undefined;
  /** Edit mode: seats were dragged. Selected seats move together; an unselected one moves alone. */
  onSeatsMoved?: ((seatIds: string[], dx: number, dy: number) => void) | undefined;
  /** Edit mode: a row's label was dragged. */
  onRowMoved?: ((rowId: string, dx: number, dy: number) => void) | undefined;
  /** The table the operator has selected; drawn with a heavier stroke. */
  selectedTableId?: string | null | undefined;
  onTableClick?: ((table: TableGeometry) => void) | undefined;
  /** Edit mode: a table was dragged. Its seats move with it. */
  onTableMoved?: ((tableId: string, dx: number, dy: number) => void) | undefined;
  /** Edit mode: a row's end handle was dragged out by `count` seats. */
  onRowExtend?: ((rowId: string, count: number, end: 'END' | 'START') => void) | undefined;
  /** Double-clicking a row's letter renames it (D-291): the same handle you drag it by. */
  onRowRename?: ((rowId: string) => void) | undefined;
  polygonTool?: PolygonTool | undefined;
  /** Vertices changed while editing a polygon (drag or removal). */
  onPolygonChange?: ((points: Point[]) => void) | undefined;
  /** A drawn polygon was closed. */
  onPolygonComplete?: ((points: Point[]) => void) | undefined;
  /** Pixel size; defaults to filling the parent element's width at a 4:3 ratio. */
  width?: number;
  height?: number;
  /** Seat radius in canvas units; estimated from seat spacing when omitted. */
  seatRadius?: number;
  /** Explicit fills by seat id (an inventory state legend, say); wins over availability and attribute coloring. */
  seatFills?: Readonly<Record<string, string>> | undefined;
  /**
   * General admission, drawn as somewhere you can buy rather than as decoration
   * (D-184). Without these a GA area is an inert dashed outline labeled with
   * its manifest capacity — a number that stops being true the moment one
   * sells.
   */
  gaAvailable?: ReadonlyMap<string, number> | undefined;
  /** How many of each area this customer is holding, so the shape can say so. */
  gaInCart?: ReadonlyMap<string, number> | undefined;
  /** Price per ticket in cents, for the shape's own label. */
  gaPriceCents?: ReadonlyMap<string, number> | undefined;
  /** Makes the shapes live. A sold-out area stays visible and stays unclickable. */
  /**
   * `at` is where the pointer was, in pixels inside the map's own container, so
   * a caller can anchor a panel to the shape that was clicked without knowing
   * anything about the stage transform.
   */
  onGaAreaClick?: ((area: GaAreaGeometry, at: { x: number; y: number } | null) => void) | undefined;
  /**
   * Draw a block for a GA area nobody has outlined, so it is on the picture the
   * customer buys from. Off in the editor, whose job is to show that the
   * outline is missing rather than to paper over it.
   */
  autoPlaceGa?: boolean | undefined;
  /** Colors to draw with, over the defaults (D-367). Anything not given keeps its default. */
  palette?: Partial<SeatMapPalette> | undefined;
  className?: string;
}

/**
 * Money on a shape's label.
 *
 * Local and tiny on purpose: this package renders geometry and must not grow a
 * dependency on an app's formatting, but a GA area that does not say its price
 * is asking somebody to click and find out.
 */
const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/**
 * Every color the map draws with (D-367).
 *
 * Deliberately few: state must be readable at a glance and by someone
 * color-blind (stroke width and hollowness carry meaning too). These used to be
 * one hardcoded burgundy-and-ochre set, which is fine in the admin editor and
 * wrong on a storefront drawn in somebody else's colors, so a page can now pass
 * its own through `palette`. Price zones never come from here: a zone's color is
 * `price_zones.color_hex`, passed in as `seatFills`.
 */
export interface SeatMapPalette {
  seat: string;
  seatStroke: string;
  available: string;
  heldByYou: string;
  /** A seat the buyer has just chosen: green, with a tick inside. */
  chosen: string;
  unavailable: string;
  accessibleRequest: string;
  accessible: string;
  companion: string;
  restricted: string;
  inactive: string;
  selected: string;
  section: string;
  sectionStroke: string;
  drawnStroke: string;
  ga: string;
  gaStroke: string;
  /** Held by this customer: the same ink the rest of the map uses for "yours". */
  gaMine: string;
  gaMineStroke: string;
  /** Gone. Hollow and gray, the way a sold seat is, so it reads without color. */
  gaGone: string;
  gaGoneStroke: string;
  table: string;
  tableStroke: string;
  label: string;
  handle: string;
  ghost: string;
  /** Behind the drawing, where the map can be panned past its edge. */
  stage: string;
  /** The drawing's own sheet, and its edge. */
  canvas: string;
  canvasStroke: string;
  /** The zoom buttons. */
  control: string;
  controlStroke: string;
  controlInk: string;
}

export const DEFAULT_SEATMAP_PALETTE: SeatMapPalette = {
  seat: '#d9d9d9',
  seatStroke: '#9a9a9a',
  available: '#7b1e2b',
  heldByYou: '#b3893f',
  chosen: '#15803d',
  unavailable: '#d9d9d9',
  accessibleRequest: '#2f6fb3',
  accessible: '#2f6fb3',
  companion: '#6d9ad6',
  restricted: '#d99a2b',
  inactive: '#ffffff',
  selected: '#171a21',
  section: 'rgba(123, 30, 43, 0.06)',
  sectionStroke: 'rgba(123, 30, 43, 0.35)',
  drawnStroke: 'rgba(123, 30, 43, 0.7)',
  ga: 'rgba(47, 111, 179, 0.12)',
  gaStroke: 'rgba(47, 111, 179, 0.6)',
  gaMine: 'rgba(23, 26, 33, 0.16)',
  gaMineStroke: '#171a21',
  gaGone: 'rgba(120, 126, 138, 0.10)',
  gaGoneStroke: 'rgba(120, 126, 138, 0.55)',
  table: '#efe6d3',
  tableStroke: '#b3893f',
  label: '#4d5566',
  handle: '#7b1e2b',
  ghost: 'rgba(123, 30, 43, 0.25)',
  stage: '#f7f8fa',
  canvas: '#ffffff',
  canvasStroke: '#e3e6ec',
  control: '#ffffff',
  controlStroke: '#e3e6ec',
  controlInk: '#171a21',
};

/**
 * Ink that reads on whatever the seat is filled with.
 *
 * A wheelchair space is blue when it is for sale and gray when it is gone, and
 * the mark has to stay legible on both — so the contrast is computed from the
 * fill rather than guessed once.
 */
export function inkFor(fill: string): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(fill.trim());
  if (!hex) return '#0f172a';
  const value = Number.parseInt(hex[1]!, 16);
  const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  // Rec. 709 luma, which is close enough to perceived lightness for this.
  const luma = (0.2126 * r! + 0.7152 * g! + 0.0722 * b!) / 255;
  return luma > 0.6 ? '#0f172a' : '#ffffff';
}

/**
 * The international symbol of access, drawn on a seat (D-91).
 *
 * The proportions are the real symbol's, in a box of half-size `0.82r` so the
 * glyph keeps a margin inside the dot: a large wheel filling the lower two
 * thirds, the figure sitting on its upper left, the head clear above it. What
 * makes it read at this size is that the figure is bold and the wheel rim is
 * thin — drawn at one weight the whole thing turns into a letter "b".
 *
 * It is built from primitives rather than a font or an emoji, which would
 * render differently on every machine and may not be installed at all.
 */
function accessibleMark(cx: number, cy: number, r: number, ink: string): ReactElement {
  const h = r * 0.82;
  const at = (bx: number, by: number): [number, number] => [cx + bx * h, cy + by * h];
  // In canvas units, so the glyph scales as one piece: a rim that stayed the
  // same width on screen while the wheel grew would stop looking like a wheel.
  const body = Math.max(0.8, h * 0.22);
  const rim = Math.max(0.6, h * 0.14);
  const [headX, headY] = at(-0.45, -0.72);
  const [backX, backY] = at(-0.42, -0.42);
  const [hipX, hipY] = at(-0.18, 0.06);
  const [kneeX, kneeY] = at(0.46, 0.12);
  const [footX, footY] = at(0.56, 0.62);
  const [wheelX, wheelY] = at(0.1, 0.3);
  return (
    <Group key="mark" listening={false}>
      <Circle x={wheelX} y={wheelY} radius={h * 0.62} stroke={ink} strokeWidth={rim} listening={false} />
      <Circle x={headX} y={headY} radius={h * 0.2} fill={ink} listening={false} />
      <Line points={[backX, backY, hipX, hipY, kneeX, kneeY]} stroke={ink} strokeWidth={body} lineCap="round" lineJoin="round" listening={false} />
      <Line points={[kneeX, kneeY, footX, footY]} stroke={ink} strokeWidth={body * 0.8} lineCap="round" listening={false} />
    </Group>
  );
}

function parsePolygon(json: string | null): PolygonGeometry | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<PolygonGeometry>;
    if (parsed.kind === 'polygon' && Array.isArray(parsed.points) && parsed.points.length >= 3) return parsed as PolygonGeometry;
  } catch {
    // Malformed geometry is a drawing problem, not a reason to fail the map.
  }
  return null;
}

function useBackgroundImage(url: string | null | undefined): HTMLImageElement | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) {
      setImage(null);
      return;
    }
    let canceled = false;
    let objectUrl: string | null = null;
    // Fetched rather than assigned to `img.src` so the session cookie goes
    // along and the response type is honored for SVG.
    fetch(url, { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`background ${response.status}`);
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        const img = new window.Image();
        img.onload = () => {
          if (!canceled) setImage(img);
        };
        img.src = objectUrl;
      })
      .catch(() => {
        if (!canceled) setImage(null);
      });
    return () => {
      canceled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return image;
}

interface RowInfo {
  id: string;
  label: string;
  seats: SeatGeometry[];
  first: SeatGeometry;
  last: SeatGeometry;
  direction: Point;
  spacing: number;
}

export function SeatMapView({
  layout,
  mode = 'view',
  backgroundUrl,
  availability,
  selectedSeatIds,
  ownedSeatIds,
  onSeatClick,
  onMarqueeSelect,
  onCanvasClick,
  onSectionClick,
  onSeatsMoved,
  onRowMoved,
  selectedTableId,
  onTableClick,
  onTableMoved,
  onRowExtend,
  onRowRename,
  polygonTool,
  onPolygonChange,
  onPolygonComplete,
  width,
  height,
  seatRadius,
  seatFills,
  gaAvailable,
  gaInCart,
  gaPriceCents,
  onGaAreaClick,
  autoPlaceGa = false,
  palette,
  className,
}: SeatMapViewProps) {
  const COLORS = useMemo<SeatMapPalette>(() => ({ ...DEFAULT_SEATMAP_PALETTE, ...palette }), [palette]);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  // Nothing is drawn until the container has been measured: an unmeasured 800px canvas would widen a phone's page instead of fitting it.
  const [size, setSize] = useState({ width: width ?? 0, height: height ?? 0 });
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [shiftHeld, setShiftHeld] = useState(false);
  const [marquee, setMarquee] = useState<{ start: Point; end: Point } | null>(null);
  const [draft, setDraft] = useState<Point[]>([]);
  const [extending, setExtending] = useState<{ rowId: string; end: 'END' | 'START'; count: number } | null>(null);
  const background = useBackgroundImage(backgroundUrl);

  const estimatedRadius = useMemo(() => estimateSeatRadius(layout.seats), [layout.seats]);
  const radius = seatRadius ?? estimatedRadius;
  const canvasW = layout.manifest.canvas_width;
  /*
   * The top-left of the visible window (D-267).
   *
   * A canvas used to be a size with an implied origin of 0,0, so trimming it
   * cropped the bottom -- useless for an imported artboard whose emptiness is
   * above the seats. These two numbers make it a window instead, and they are
   * 0,0 for every map nobody has cropped, so nothing that existed moves.
   */
  const canvasX = layout.manifest.canvas_x ?? 0;
  const canvasY = layout.manifest.canvas_y ?? 0;

  /*
   * Where each GA area is drawn (D-184).
   *
   * An area may hold several outlines that are one pool of capacity, and most
   * hold none at all — nobody has opened the editor and drawn a lawn. An area
   * with nothing drawn gets a block laid out below everything else, worked out
   * here and never stored, so a wrong guess costs nothing and vanishes the
   * moment somebody draws the real thing.
   */
  const gaShapes = useMemo(() => {
    const drawn = layout.ga_areas.map((area) => ({ area, shapes: shapesOf(area.geometry_json), auto: false }));
    if (!autoPlaceGa) return { areas: drawn, height: layout.manifest.canvas_height };
    const missing = drawn.filter((entry) => entry.shapes.length === 0);
    if (missing.length === 0) return { areas: drawn, height: layout.manifest.canvas_height };
    // Below everything already on the picture, not on top of it.
    const taken: Point[] = [
      ...layout.seats.map((seat) => [seat.x, seat.y] as Point),
      ...layout.tables.map((table) => [table.x, table.y + table.height / 2] as Point),
      ...drawn.flatMap((entry) => entry.shapes.flat()),
      ...layout.sections.flatMap((section) => shapesOf(section.geometry_json).flat()),
    ];
    const placed = autoPlaceGaBlocks(
      missing.map(({ area }) => ({ id: area.id, code: area.code, capacity: area.capacity })),
      layout.manifest.canvas_width,
      boundsOf(taken),
    );
    const byArea = new Map(placed.blocks.map((block) => [block.ga_area_id, block.points]));
    return {
      areas: drawn.map((entry) => (entry.shapes.length > 0 ? entry : { ...entry, shapes: byArea.has(entry.area.id) ? [byArea.get(entry.area.id)!] : [], auto: true })),
      height: Math.max(layout.manifest.canvas_height, placed.canvas_height),
    };
  }, [layout, autoPlaceGa]);

  // The canvas grows to fit anything placed below the room.
  const canvasH = gaShapes.height;
  const editing = mode === 'edit';
  const drawing = polygonTool?.mode === 'draw';

  // Touch events keep firing while the stage is being dragged, so a second finger can turn a pan into a pinch.
  useEffect(() => {
    Konva.hitOnDragEnabled = true;
  }, []);

  // Size to the container unless told otherwise (before paint, so the first frame is already the right size).
  useLayoutEffect(() => {
    if (width && height) {
      setSize({ width, height });
      return;
    }
    const element = containerRef.current;
    if (!element) return;
    const update = () => {
      const w = width ?? element.clientWidth;
      if (!w) return;
      setSize({ width: w, height: height ?? Math.max(360, Math.round(w * 0.72)) });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [width, height]);

  const fit = useCallback(() => {
    const scale = Math.min(size.width / canvasW, size.height / canvasH) * 0.96;
    // Center the window, not the origin: with a crop, the drawing starts at
    // canvasX/canvasY and everything left of it is not meant to be on screen.
    setView({ scale, x: (size.width - canvasW * scale) / 2 - canvasX * scale, y: (size.height - canvasH * scale) / 2 - canvasY * scale });
  }, [size.width, size.height, canvasW, canvasH, canvasX, canvasY]);

  useEffect(() => {
    fit();
  }, [fit]);

  useEffect(() => {
    setDraft([]);
  }, [polygonTool?.mode]);

  useEffect(() => {
    if (!editing) return;
    const down = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setShiftHeld(true);
    };
    const up = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setShiftHeld(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [editing]);

  const toCanvas = useCallback(
    (pointer: { x: number; y: number }): Point => [(pointer.x - view.x) / view.scale, (pointer.y - view.y) / view.scale],
    [view],
  );
  const pointerOnCanvas = (): Point | null => {
    const pointer = stageRef.current?.getPointerPosition();
    return pointer ? toCanvas(pointer) : null;
  };

  const onWheel = (event: KonvaEventObject<WheelEvent>) => {
    event.evt.preventDefault();
    const pointer = stageRef.current?.getPointerPosition();
    if (!pointer) return;
    const factor = event.evt.deltaY < 0 ? 1.12 : 1 / 1.12;
    const scale = Math.min(12, Math.max(0.05, view.scale * factor));
    const [cx, cy] = toCanvas(pointer);
    setView({ scale, x: pointer.x - cx * scale, y: pointer.y - cy * scale });
  };

  const onStageDragEnd = (event: KonvaEventObject<DragEvent>) => {
    if (event.target !== stageRef.current) return;
    setView((current) => ({ ...current, x: event.target.x(), y: event.target.y() }));
  };

  // Two fingers on a touch screen: zoom about their midpoint and follow it as it moves (one finger still pans, via the
  // stage drag, which stops the moment a second finger lands so the two gestures never fight).
  const pinchRef = useRef<{ dist: number; center: { x: number; y: number } } | null>(null);
  const onTouchMove = (event: KonvaEventObject<TouchEvent>) => {
    const touches = event.evt.touches;
    if (touches.length < 2) return;
    event.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    if (stage.isDragging()) stage.stopDrag();
    const rect = stage.container().getBoundingClientRect();
    const a = { x: touches[0]!.clientX - rect.left, y: touches[0]!.clientY - rect.top };
    const b = { x: touches[1]!.clientX - rect.left, y: touches[1]!.clientY - rect.top };
    const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    const last = pinchRef.current;
    pinchRef.current = { dist, center };
    if (!last || last.dist === 0) return;
    setView((current) => {
      const scale = Math.min(12, Math.max(0.05, current.scale * (dist / last.dist)));
      const cx = (last.center.x - current.x) / current.scale;
      const cy = (last.center.y - current.y) / current.scale;
      return { scale, x: center.x - cx * scale, y: center.y - cy * scale };
    });
  };
  const onTouchEnd = () => {
    pinchRef.current = null;
  };

  const marqueeActive = editing && shiftHeld && !polygonTool;

  const onMouseDown = (event: KonvaEventObject<MouseEvent>) => {
    if (!marqueeActive || event.target !== stageRef.current) return;
    const point = pointerOnCanvas();
    if (!point) return;
    setMarquee({ start: point, end: point });
    event.cancelBubble = true;
  };
  const onMouseMove = () => {
    if (!marquee) return;
    const point = pointerOnCanvas();
    if (point) setMarquee({ start: marquee.start, end: point });
  };
  const onMouseUp = (event: KonvaEventObject<MouseEvent>) => {
    if (!marquee) return;
    const bounds = boundsOf([marquee.start, marquee.end])!;
    const ids = layout.seats.filter((seat) => pointInBounds([seat.x, seat.y], bounds)).map((seat) => seat.id);
    setMarquee(null);
    onMarqueeSelect?.(ids, event.evt.shiftKey);
  };

  const closeThreshold = () => (radius * 1.5) / Math.max(view.scale, 0.2) + radius;

  const onStageClick = (event: KonvaEventObject<MouseEvent>) => {
    if (drawing) {
      const point = pointerOnCanvas();
      if (!point) return;
      const first = draft[0];
      if (first && draft.length >= 3 && Math.hypot(point[0] - first[0], point[1] - first[1]) < closeThreshold()) {
        onPolygonComplete?.(draft);
        setDraft([]);
        return;
      }
      setDraft([...draft, [round2(point[0]), round2(point[1])]]);
      return;
    }
    if (event.target !== stageRef.current || !onCanvasClick) return;
    const point = pointerOnCanvas();
    if (point) onCanvasClick(point);
  };
  const onStageDblClick = () => {
    if (drawing && draft.length >= 3) {
      onPolygonComplete?.(draft);
      setDraft([]);
    }
  };

  // --- Seats -----------------------------------------------------------------

  /** Picked by the buyer: either just clicked, or already held in their cart. */
  const chosenSeat = (seat: SeatGeometry): boolean =>
    mode === 'view' && !ownedSeatIds?.has(seat.id) && ((selectedSeatIds?.has(seat.id) ?? false) || availability?.[seat.id] === 'HELD_BY_YOU');

  const seatStyle = (seat: SeatGeometry): { fill: string; stroke: string; strokeWidth: number; dash?: number[] } => {
    const selected = selectedSeatIds?.has(seat.id) ?? false;
    if (ownedSeatIds?.has(seat.id)) return { fill: COLORS.heldByYou, stroke: COLORS.heldByYou, strokeWidth: 2 };
    if (chosenSeat(seat)) return { fill: COLORS.chosen, stroke: COLORS.chosen, strokeWidth: 2.5 };
    // A wheelchair space or its companion reads as itself, not as its price zone: the
    // zone fill used to hide them among ordinary seats (D-84).
    if (mode === 'view' && availability?.[seat.id] === 'ACCESSIBLE_ON_REQUEST') {
      return { fill: COLORS.accessibleRequest, stroke: COLORS.accessibleRequest, strokeWidth: 2 };
    }
    let fill = COLORS.seat;
    let stroke = COLORS.seatStroke;
    let dash: number[] | undefined;
    const explicit = seatFills?.[seat.id];
    if (explicit) {
      fill = explicit;
      stroke = explicit === COLORS.seat ? COLORS.seatStroke : explicit;
    } else if (mode === 'view' && availability) {
      const state = availability[seat.id] ?? 'UNAVAILABLE';
      fill =
        state === 'AVAILABLE'
          ? COLORS.available
          : state === 'HELD_BY_YOU'
            ? COLORS.heldByYou
            : state === 'ACCESSIBLE_ON_REQUEST'
              ? COLORS.accessibleRequest
              : COLORS.unavailable;
      stroke = fill === COLORS.unavailable ? COLORS.seatStroke : fill;
    } else {
      if (seat.accessible) {
        fill = COLORS.accessible;
        stroke = COLORS.accessible;
      } else if (seat.companion) {
        fill = COLORS.companion;
        stroke = COLORS.accessible;
        dash = [2, 2];
      }
      if (seat.restricted_view || seat.obstructed_view) stroke = COLORS.restricted;
      if (!seat.active) fill = COLORS.inactive;
    }
    // Chosen seats read as chosen at a glance — green, heavier stroke, and a tick (drawn in seatNode).
    if (selected) return { fill: mode === 'view' ? COLORS.chosen : fill, stroke: mode === 'view' ? COLORS.chosen : COLORS.selected, strokeWidth: 3, ...(dash ? { dash } : {}) };
    return { fill, stroke, strokeWidth: seat.restricted_view || seat.obstructed_view ? 2 : 1, ...(dash ? { dash } : {}) };
  };

  const strokeScale = Math.max(0.5, Math.min(view.scale, 2));
  // A table drags as one piece: the shape, its label and its seats. The seats live in
  // another layer, so their offset is tracked here until the drag ends.
  const tablesDraggable = editing && Boolean(onTableMoved) && !polygonTool;
  const [tableDrag, setTableDrag] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const seatOffset = (seat: SeatGeometry): Point => (tableDrag && seat.table_id === tableDrag.id ? [tableDrag.dx, tableDrag.dy] : [0, 0]);
  const seatsDraggable = editing && Boolean(onSeatsMoved) && !shiftHeld && !polygonTool;
  const selectedSeats = useMemo(
    () => (selectedSeatIds && selectedSeatIds.size > 0 ? layout.seats.filter((seat) => selectedSeatIds.has(seat.id)) : []),
    [layout.seats, selectedSeatIds],
  );
  const unselectedSeats = useMemo(
    () => (selectedSeatIds && selectedSeatIds.size > 0 ? layout.seats.filter((seat) => !selectedSeatIds.has(seat.id)) : layout.seats),
    [layout.seats, selectedSeatIds],
  );

  const seatNode = (seat: SeatGeometry, draggableAlone: boolean) => {
    const style = seatStyle(seat);
    const [ox, oy] = seatOffset(seat);
    const chosen = chosenSeat(seat);
    const owned = ownedSeatIds?.has(seat.id) ?? false;
    const clickable = Boolean(onSeatClick) && !polygonTool && !owned;
    const circle = (
      <Circle
        key={seat.id}
        x={seat.x + ox}
        y={seat.y + oy}
        radius={radius}
        fill={style.fill}
        stroke={style.stroke}
        strokeWidth={style.strokeWidth / strokeScale}
        {...(style.dash ? { dash: style.dash } : {})}
        perfectDrawEnabled={false}
        shadowForStrokeEnabled={false}
        hitStrokeWidth={0}
        draggable={draggableAlone}
        {...(draggableAlone
          ? {
              onDragEnd: (event: KonvaEventObject<DragEvent>) => {
                const dx = round2(event.target.x() - seat.x);
                const dy = round2(event.target.y() - seat.y);
                event.target.position({ x: seat.x, y: seat.y });
                if (dx !== 0 || dy !== 0) onSeatsMoved?.([seat.id], dx, dy);
              },
            }
          : {})}
        {...(clickable
          ? {
              onClick: (event: KonvaEventObject<MouseEvent>) =>
                onSeatClick!(seat, { shiftKey: event.evt.shiftKey, metaKey: event.evt.metaKey || event.evt.ctrlKey }),
              onTap: () => onSeatClick!(seat, { shiftKey: false, metaKey: false }),
            }
          : {})}
      />
    );
    // A wheelchair space and its companion say so on the seat itself, not only
    // in color, so they can be told apart by anyone (D-91).
    const mark = showSeatMarks && !chosen && !owned && (seat.accessible || seat.companion) ? (seat.accessible ? 'ACCESSIBLE' : 'COMPANION') : null;
    if (mark) {
      const ink = inkFor(style.fill);
      return (
        <Group key={`${seat.id}-mark`} listening={clickable}>
          {circle}
          {mark === 'ACCESSIBLE' ? (
            accessibleMark(seat.x + ox, seat.y + oy, radius, ink)
          ) : (
            <Text
              x={seat.x + ox - radius}
              y={seat.y + oy - radius}
              width={radius * 2}
              height={radius * 2}
              align="center"
              verticalAlign="middle"
              text="C"
              fontSize={radius * 1.25}
              fontStyle="bold"
              fill={ink}
              listening={false}
            />
          )}
        </Group>
      );
    }
    if (!chosen) return circle;
    // A tick inside the dot, so the choice is readable without relying on color alone.
    const r = radius;
    return (
      <Group key={`${seat.id}-chosen`} listening={clickable}>
        {circle}
        <Line
          points={[seat.x + ox - r * 0.45, seat.y + oy + r * 0.05, seat.x + ox - r * 0.1, seat.y + oy + r * 0.42, seat.x + ox + r * 0.5, seat.y + oy - r * 0.4]}
          stroke="#ffffff"
          strokeWidth={Math.max(1, r * 0.28)}
          lineCap="round"
          lineJoin="round"
          listening={false}
        />
      </Group>
    );
  };

  // --- Rows: labels as drag handles, end handles to extend ---------------------

  const rows = useMemo<RowInfo[]>(() => {
    const byRow = new Map<string, SeatGeometry[]>();
    for (const seat of layout.seats) {
      if (!seat.row_id) continue;
      const list = byRow.get(seat.row_id) ?? [];
      list.push(seat);
      byRow.set(seat.row_id, list);
    }
    return layout.rows.flatMap((row) => {
      const seats = (byRow.get(row.id) ?? []).sort((a, b) => a.sort_order - b.sort_order);
      if (seats.length === 0) return [];
      const axis = rowAxis(seats);
      return [{ id: row.id, label: row.label, seats, first: seats[0]!, last: seats[seats.length - 1]!, direction: axis.direction, spacing: axis.spacing }];
    });
  }, [layout.rows, layout.seats]);

  const showRowLabels = view.scale * radius > (editing ? 2.5 : 4);
  // A wheelchair glyph needs about twenty pixels of seat to read; below that
  // the color, the seat list and the legend carry the meaning instead.
  const showSeatMarks = view.scale * radius > 11;
  const showHandles = editing && Boolean(onRowExtend) && view.scale * radius > 3 && !polygonTool;

  const rowLabelNode = (row: RowInfo) => {
    const x = row.first.x - row.direction[0] * radius * 3 - radius;
    const y = row.first.y - row.direction[1] * radius * 3 - radius;
    const draggable = editing && Boolean(onRowMoved) && !polygonTool;
    /*
     * Rename on a double-click of the letter (D-291), which is the same handle
     * the row is dragged by. Renaming was only reachable from the selection
     * inspector, which means selecting a seat first -- and nobody looking to
     * rename a row thinks to click a seat.
     */
    const renameable = editing && Boolean(onRowRename) && !polygonTool;
    return (
      <Group
        key={row.id}
        x={x}
        y={y}
        draggable={draggable}
        {...(renameable
          ? {
              onDblClick: () => onRowRename?.(row.id),
              onDblTap: () => onRowRename?.(row.id),
            }
          : {})}
        {...(draggable
          ? {
              onDragEnd: (event: KonvaEventObject<DragEvent>) => {
                const dx = round2(event.target.x() - x);
                const dy = round2(event.target.y() - y);
                event.target.position({ x, y });
                if (dx !== 0 || dy !== 0) onRowMoved?.(row.id, dx, dy);
              },
            }
          : draggable || renameable
            ? {}
            : { listening: false })}
      >
        {draggable ? <Rect width={radius * 2} height={radius * 2} cornerRadius={radius * 0.4} fill="rgba(255,255,255,0.85)" stroke={COLORS.seatStroke} strokeWidth={1 / strokeScale} /> : null}
        <Text width={radius * 2} height={radius * 2} align="center" verticalAlign="middle" text={row.label} fontSize={radius * 1.3} fill={COLORS.label} listening={false} />
      </Group>
    );
  };

  const extendHandle = (row: RowInfo, end: 'END' | 'START') => {
    const edge = end === 'END' ? row.last : row.first;
    const sign = end === 'END' ? 1 : -1;
    const hx = edge.x + row.direction[0] * row.spacing * sign;
    const hy = edge.y + row.direction[1] * row.spacing * sign;
    const active = extending?.rowId === row.id && extending.end === end ? extending : null;
    return (
      <Group key={`${row.id}-${end}`}>
        {active
          ? Array.from({ length: active.count }, (_, i) => (
              <Circle
                key={i}
                x={edge.x + row.direction[0] * row.spacing * sign * (i + 1)}
                y={edge.y + row.direction[1] * row.spacing * sign * (i + 1)}
                radius={radius}
                fill={COLORS.ghost}
                listening={false}
              />
            ))
          : null}
        <Circle
          x={hx}
          y={hy}
          radius={radius * 0.6}
          fill="#ffffff"
          stroke={COLORS.handle}
          strokeWidth={1.5 / strokeScale}
          draggable
          onDragMove={(event) => {
            const along = (event.target.x() - hx) * row.direction[0] * sign + (event.target.y() - hy) * row.direction[1] * sign;
            const count = Math.max(0, Math.min(100, Math.round(along / row.spacing) + 1));
            setExtending({ rowId: row.id, end, count });
          }}
          onDragEnd={(event) => {
            const count = extending?.rowId === row.id && extending.end === end ? extending.count : 0;
            event.target.position({ x: hx, y: hy });
            setExtending(null);
            if (count > 0) onRowExtend?.(row.id, count, end);
          }}
        />
      </Group>
    );
  };

  // --- Polygons --------------------------------------------------------------

  const vertexRadius = Math.max(radius * 0.7, 4 / view.scale);
  const editablePolygon = polygonTool?.mode === 'edit' ? polygonTool.points : null;

  return (
    <div ref={containerRef} className={className} style={{ position: 'relative', width: width ? `${width}px` : '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden', touchAction: 'none' }}>
      {size.width > 0 ? (
        <Stage
          ref={stageRef}
          width={size.width}
          height={size.height}
          x={view.x}
          y={view.y}
          scaleX={view.scale}
          scaleY={view.scale}
          draggable={!marqueeActive && !drawing}
          onWheel={onWheel}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onDragEnd={onStageDragEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onClick={onStageClick}
          onDblClick={onStageDblClick}
          style={{ background: COLORS.stage, cursor: marqueeActive || drawing ? 'crosshair' : 'grab' }}
        >
          <Layer listening={false}>
            <Rect x={canvasX} y={canvasY} width={canvasW} height={canvasH} fill={COLORS.canvas} stroke={COLORS.canvasStroke} strokeWidth={1 / view.scale} />
            {background ? <KonvaImage image={background} x={canvasX} y={canvasY} width={canvasW} height={canvasH} /> : null}
          </Layer>

          <Layer listening={!drawing}>
            {layout.sections.map((section) => {
              const polygon = parsePolygon(section.geometry_json);
              return (
                <Group key={section.id}>
                  {polygon ? (
                    <Line
                      points={polygon.points.flat()}
                      closed
                      fill={COLORS.section}
                      stroke={polygon.source === 'drawn' ? COLORS.drawnStroke : COLORS.sectionStroke}
                      strokeWidth={1 / view.scale}
                      {...(polygon.source === 'drawn' ? {} : { dash: [3 / view.scale, 3 / view.scale] })}
                      {...(onSectionClick ? { onClick: () => onSectionClick(section) } : {})}
                      perfectDrawEnabled={false}
                    />
                  ) : null}
                  {section.label_x !== null && section.label_y !== null ? (
                    <Text
                      x={section.label_x - 60}
                      y={section.label_y - 8}
                      width={120}
                      align="center"
                      text={section.code}
                      fontSize={Math.max(10, radius * 2.2)}
                      fontStyle="bold"
                      fill={COLORS.label}
                      listening={false}
                      opacity={0.7}
                    />
                  ) : null}
                </Group>
              );
            })}

            {gaShapes.areas.flatMap(({ area, shapes, auto }) => {
              /*
               * A GA area is somewhere you can buy, so it says what a seat says:
               * what it is, what it costs, how much is left, and whether any of
               * it is already yours (D-184). It used to print the manifest's
               * total capacity, which stops being true at the first sale.
               */
              const left = gaAvailable?.get(area.id);
              const mine = gaInCart?.get(area.id) ?? 0;
              const price = gaPriceCents?.get(area.id);
              const soldOut = left === 0;
              // Sold out stays drawn and stops being clickable: somebody told
              // "the lawn" needs to see that the lawn is gone (D-84's rule).
              const live = Boolean(onGaAreaClick) && !soldOut;
              const fill = mine > 0 ? COLORS.gaMine : soldOut ? COLORS.gaGone : COLORS.ga;
              const stroke = mine > 0 ? COLORS.gaMineStroke : soldOut ? COLORS.gaGoneStroke : COLORS.gaStroke;
              const detail = soldOut
                ? 'Sold out'
                : [price === undefined ? null : dollars(price), left === undefined ? null : `${left} left`].filter(Boolean).join(' · ');
              const size = Math.max(10, radius * 2);
              return shapes.map((pts, index) => {
                /*
                 * The name sits near the top of the shape, not at its middle
                 * (D-208). A GA area drawn around a floor of tables has its
                 * middle exactly where the tables are, so the name landed on
                 * top of them and on whatever they were labeled.
                 */
                const [cx, cy] = labelAnchor(pts, size * 1.6);
                /*
                 * The name and its detail lines stay inside the shape. A long,
                 * thin block (bleachers set along a baseline) gets its text
                 * turned along the block and sized to its thickness; anything
                 * roomier keeps the top-of-shape anchor (D-208), wrapped to the
                 * shape's width. Sizes shrink to fit, never grow past `size`.
                 */
                const frame = labelFrame(pts);
                const box = boundsOf(pts);
                const texts = [area.name, ...[detail, mine > 0 ? `${mine} in your order` : null].filter(Boolean)] as string[];
                const longestLine = Math.max(...texts.map((text) => text.length), 1);
                // A strip's text goes at its center, so only when the center is in it:
                // a U-shaped grandstand is long and thin too, but its center is the
                // walkway it wraps around, and it takes the top-down anchor instead.
                const strip = frame !== null && frame.length >= frame.thickness * 1.4 && pointInPolygon(frame.center, pts);
                const labelWidth = strip ? frame.length * 0.82 : Math.min(220, box ? (box.maxX - box.minX) * 0.9 : 220);
                const fitWidth = labelWidth / (longestLine * 0.62);
                const fitHeight = strip ? (frame.thickness * 0.8) / (texts.length * 1.2) : Infinity;
                const fontSize = Math.max(4, Math.min(size, fitWidth, fitHeight));
                const blockHeight = fontSize * 1.2 * texts.length;
                return (
                  <Group
                    key={`${area.id}:${index}`}
                    listening={live}
                    {...(live
                      ? {
                          onClick: (event: KonvaEventObject<MouseEvent>) => onGaAreaClick!(area, event.target.getStage()?.getPointerPosition() ?? null),
                          onTap: (event: KonvaEventObject<TouchEvent>) => onGaAreaClick!(area, event.target.getStage()?.getPointerPosition() ?? null),
                          onMouseEnter: (event: KonvaEventObject<MouseEvent>) => {
                            const stage = event.target.getStage();
                            if (stage) stage.container().style.cursor = 'pointer';
                          },
                          onMouseLeave: (event: KonvaEventObject<MouseEvent>) => {
                            const stage = event.target.getStage();
                            if (stage) stage.container().style.cursor = '';
                          },
                        }
                      : {})}
                  >
                    <Line
                      points={pts.flat()}
                      closed
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={(mine > 0 ? 2 : 1) / view.scale}
                      // A block nobody drew is dashed more loosely, so it reads
                      // as our guess at where the area is rather than as a wall.
                      dash={auto ? [10 / view.scale, 6 / view.scale] : [6 / view.scale, 4 / view.scale]}
                      perfectDrawEnabled={false}
                    />
                    <Group
                      x={strip ? frame.center[0] : cx}
                      y={strip ? frame.center[1] : cy}
                      rotation={strip ? frame.angle : 0}
                      listening={false}
                    >
                      {texts.map((text, lineIndex) => (
                        <Text
                          key={`${lineIndex}:${text}`}
                          x={-labelWidth / 2}
                          y={(strip ? -blockHeight / 2 : -fontSize * 0.55) + fontSize * 1.2 * lineIndex}
                          width={labelWidth}
                          align="center"
                          text={text}
                          fontSize={lineIndex === 0 ? fontSize : fontSize * 0.85}
                          fontStyle={lineIndex === 0 ? 'bold' : 'normal'}
                          fill={lineIndex === 1 && mine === 0 ? COLORS.label : lineIndex === 0 ? COLORS.label : COLORS.gaMineStroke}
                          listening={false}
                        />
                      ))}
                    </Group>
                  </Group>
                );
              });
            })}

            {layout.tables.map((table) => {
              const selected = selectedTableId === table.id;
              const interactive = Boolean(onTableClick) || tablesDraggable;
              const stroke = selected ? COLORS.available : COLORS.tableStroke;
              const strokeWidth = (selected ? 2.5 : 1) / strokeScale;
              return (
                <Group
                  key={table.id}
                  listening={interactive}
                  draggable={tablesDraggable}
                  {...(tablesDraggable
                    ? {
                        onDragMove: (event: KonvaEventObject<DragEvent>) => setTableDrag({ id: table.id, dx: event.target.x(), dy: event.target.y() }),
                        onDragEnd: (event: KonvaEventObject<DragEvent>) => {
                          const dx = round2(event.target.x());
                          const dy = round2(event.target.y());
                          event.target.position({ x: 0, y: 0 });
                          setTableDrag(null);
                          if (dx !== 0 || dy !== 0) onTableMoved?.(table.id, dx, dy);
                        },
                      }
                    : {})}
                  {...(onTableClick ? { onClick: () => onTableClick(table), onTap: () => onTableClick(table) } : {})}
                >
                  {table.shape === 'ROUND' ? (
                    <Ellipse x={table.x} y={table.y} radiusX={table.width / 2} radiusY={table.height / 2} rotation={table.rotation} fill={COLORS.table} stroke={stroke} strokeWidth={strokeWidth} />
                  ) : (
                    <Rect x={table.x} y={table.y} offsetX={table.width / 2} offsetY={table.height / 2} width={table.width} height={table.height} rotation={table.rotation} fill={COLORS.table} stroke={stroke} strokeWidth={strokeWidth} cornerRadius={3} />
                  )}
                  <Text x={table.x - 40} y={table.y - 6} width={80} align="center" text={table.label} fontSize={Math.max(9, radius * 1.6)} fill={COLORS.label} listening={false} />
                </Group>
              );
            })}
          </Layer>

          <Layer listening={!drawing}>
            {unselectedSeats.map((seat) => seatNode(seat, seatsDraggable))}
            {/* Selected seats live in one draggable group so they move together. */}
            {selectedSeats.length > 0 ? (
              <Group
                draggable={seatsDraggable}
                {...(seatsDraggable
                  ? {
                      onDragEnd: (event: KonvaEventObject<DragEvent>) => {
                        const dx = round2(event.target.x());
                        const dy = round2(event.target.y());
                        event.target.position({ x: 0, y: 0 });
                        if (dx !== 0 || dy !== 0) onSeatsMoved?.(selectedSeats.map((seat) => seat.id), dx, dy);
                      },
                    }
                  : {})}
              >
                {selectedSeats.map((seat) => seatNode(seat, false))}
              </Group>
            ) : null}
            {showRowLabels ? rows.map(rowLabelNode) : null}
            {showHandles ? rows.flatMap((row) => [extendHandle(row, 'END'), extendHandle(row, 'START')]) : null}
            {marquee ? (
              <Rect
                x={Math.min(marquee.start[0], marquee.end[0])}
                y={Math.min(marquee.start[1], marquee.end[1])}
                width={Math.abs(marquee.end[0] - marquee.start[0])}
                height={Math.abs(marquee.end[1] - marquee.start[1])}
                fill="rgba(123, 30, 43, 0.08)"
                stroke={COLORS.available}
                strokeWidth={1 / view.scale}
                dash={[4, 4]}
                listening={false}
              />
            ) : null}
          </Layer>

          {polygonTool ? (
            <Layer>
              {drawing && draft.length > 0 ? (
                <Group>
                  <Line points={draft.flat()} stroke={COLORS.drawnStroke} strokeWidth={1.5 / view.scale} dash={[4 / view.scale, 4 / view.scale]} listening={false} />
                  {draft.map((point, index) => (
                    <Circle
                      key={index}
                      x={point[0]}
                      y={point[1]}
                      radius={index === 0 && draft.length >= 3 ? vertexRadius * 1.4 : vertexRadius}
                      fill={index === 0 ? COLORS.handle : '#ffffff'}
                      stroke={COLORS.handle}
                      strokeWidth={1.5 / view.scale}
                      listening={false}
                    />
                  ))}
                </Group>
              ) : null}
              {editablePolygon ? (
                <Group>
                  <Line points={editablePolygon.flat()} closed fill={COLORS.section} stroke={COLORS.drawnStroke} strokeWidth={1.5 / view.scale} listening={false} />
                  {/* Edge midpoints: click one to add a corner there. */}
                  {editablePolygon.map((point, index) => {
                    const next = editablePolygon[(index + 1) % editablePolygon.length]!;
                    const mid: Point = [round2((point[0] + next[0]) / 2), round2((point[1] + next[1]) / 2)];
                    return (
                      <Circle
                        key={`mid-${index}`}
                        x={mid[0]}
                        y={mid[1]}
                        radius={vertexRadius * 0.7}
                        fill={COLORS.drawnStroke}
                        opacity={0.6}
                        onClick={() => onPolygonChange?.([...editablePolygon.slice(0, index + 1), mid, ...editablePolygon.slice(index + 1)])}
                        onTap={() => onPolygonChange?.([...editablePolygon.slice(0, index + 1), mid, ...editablePolygon.slice(index + 1)])}
                      />
                    );
                  })}
                  {editablePolygon.map((point, index) => (
                    <Circle
                      key={index}
                      x={point[0]}
                      y={point[1]}
                      radius={vertexRadius}
                      fill="#ffffff"
                      stroke={COLORS.handle}
                      strokeWidth={1.5 / view.scale}
                      draggable
                      onDragEnd={(event) => {
                        const next = editablePolygon.map((p, i) => (i === index ? ([round2(event.target.x()), round2(event.target.y())] as Point) : p));
                        onPolygonChange?.(next);
                      }}
                      onDblClick={() => {
                        if (editablePolygon.length > 3) onPolygonChange?.(editablePolygon.filter((_, i) => i !== index));
                      }}
                    />
                  ))}
                </Group>
              ) : null}
            </Layer>
          ) : null}
        </Stage>
      ) : null}

      <div style={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 4 }}>
        <button type="button" onClick={fit} title="Fit to view" style={{ ...buttonStyle, background: COLORS.control, border: `1px solid ${COLORS.controlStroke}`, color: COLORS.controlInk }} aria-label="Fit the whole map in view">
          ⤢
        </button>
        <button type="button" onClick={() => setView((v) => ({ ...v, scale: v.scale * 1.25 }))} title="Zoom in" style={{ ...buttonStyle, background: COLORS.control, border: `1px solid ${COLORS.controlStroke}`, color: COLORS.controlInk }} aria-label="Zoom in">
          +
        </button>
        <button type="button" onClick={() => setView((v) => ({ ...v, scale: v.scale / 1.25 }))} title="Zoom out" style={{ ...buttonStyle, background: COLORS.control, border: `1px solid ${COLORS.controlStroke}`, color: COLORS.controlInk }} aria-label="Zoom out">
          −
        </button>
      </div>
      {editing ? (
        <div style={{ position: 'absolute', left: 8, bottom: 8, fontSize: 11, color: '#7b8396', background: 'rgba(255,255,255,0.85)', padding: '2px 6px', borderRadius: 3 }}>
          {drawing
            ? `Click to add corners${draft.length >= 3 ? '; click the first corner or double-click to close' : ''}`
            : editablePolygon
              ? 'Drag a corner to move it · click a small dot on an edge to add a corner · double-click a corner to remove it'
              : 'Drag to pan · scroll or pinch to zoom · click a seat · shift-drag to select many · drag seats or a row letter to move · drag a row’s end handle to add seats'}
        </div>
      ) : null}
    </div>
  );
}

const buttonStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: '1px solid #e3e6ec',
  background: '#fff',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '26px',
  padding: 0,
};
